import React, { useEffect, useRef } from 'react';
import { Section } from '../../types/Section';
import { SectionContainer } from './SectionContainer';
import { useEventContext } from '../../contexts/EventContext';
import { useEventDetails } from '../../../hooks/useEventDetails';

interface DetailsSectionProps {
    section: Section;
}

export const DetailsSection: React.FC<DetailsSectionProps> = ({ section }) => {
    const contentRef = useRef<HTMLDivElement>(null);

    // Get event primary color
    const { eventSlug, style: contextStyle } = useEventContext();
    const { data: eventDetails } = useEventDetails(eventSlug);
    const eventStyle = contextStyle || eventDetails?.style;
    const primaryColor = eventStyle?.headerColor || '#007bff';

    // Apply fallback classes for floated images in links and empty paragraphs
    useEffect(() => {
        if (contentRef.current) {
            // Find all anchors containing floated images
            const anchorsWithFloatLeft = contentRef.current.querySelectorAll('a > img.media-float-left');
            anchorsWithFloatLeft.forEach(img => {
                const anchor = img.parentElement as HTMLAnchorElement;
                if (anchor) {
                    anchor.classList.add('has-float-left');
                }
            });

            const anchorsWithFloatRight = contentRef.current.querySelectorAll('a > img.media-float-right');
            anchorsWithFloatRight.forEach(img => {
                const anchor = img.parentElement as HTMLAnchorElement;
                if (anchor) {
                    anchor.classList.add('has-float-right');
                }
            });

            // Handle paragraphs that only contain <br> tags or whitespace
            const paragraphs = contentRef.current.querySelectorAll('p');
            paragraphs.forEach(p => {
                // Check if paragraph only contains br tags and/or whitespace
                const hasOnlyBrOrWhitespace =
                    p.childNodes.length === 0 || // Empty
                    (p.childNodes.length === 1 && p.childNodes[0].nodeName === 'BR') || // Only <br>
                    (p.textContent?.trim() === '' && p.querySelectorAll('*').length === p.querySelectorAll('br').length); // Only br tags and whitespace

                if (hasOnlyBrOrWhitespace) {
                    p.classList.add('empty-paragraph');
                }
            });

            // Handle empty divs or divs with only <br> tags
            const divs = contentRef.current.querySelectorAll('div');
            divs.forEach(div => {
                // Check if div is empty or only contains br tags and/or whitespace
                const hasOnlyBrOrWhitespace =
                    div.childNodes.length === 0 || // Empty
                    (div.childNodes.length === 1 && div.childNodes[0].nodeName === 'BR') || // Only <br>
                    (div.textContent?.trim() === '' && div.querySelectorAll('*').length === div.querySelectorAll('br').length); // Only br tags and whitespace

                if (hasOnlyBrOrWhitespace) {
                    div.classList.add('empty-div');
                }
            });
        }
    }, [section.content]);

    return (
        <>
            <SectionContainer section={section} className="flex flex-col w-full">
                <h2 className="text-2xl font-bold">{section.title}</h2>
                <div
                    ref={contentRef}
                    className="mt-4 tinymce-content tinymce-content-buttons flex-grow"
                    style={{ '--primary-color': primaryColor } as React.CSSProperties}
                    dangerouslySetInnerHTML={{ __html: section.content || '' }}
                />
            </SectionContainer>
        </>
    );
};
